import type { ContractImportJobDetail } from './types';

import {
  type QueryFunctionContext,
  queryOptions,
  skipToken,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { type UUID } from '~/types/common';

import { VestigasHTTPError } from '@vestigas/data';

import { queryKeysContractImportJob } from '../queryKeys';

import { fetchContractImportJob } from './fetchContractImportJob';

/**
 * Generates the query options for the contract import job detail query.
 *
 * @param jobId - The ID of the import job to fetch.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getContractImportJobQueryOptions = <
  TData = ContractImportJobDetail,
>({
  jobId,
  options,
}: {
  jobId: UUID | undefined;
  options?: Omit<
    UseQueryOptions<ContractImportJobDetail, VestigasHTTPError, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    queryFn: jobId
      ? async ({ signal }: QueryFunctionContext) =>
          fetchContractImportJob(jobId, { signal })
      : skipToken,
    queryKey: queryKeysContractImportJob.get(jobId ?? ''),
    staleTime: ms('30s'),
    ...options,
  });

/**
 * React Query based custom hook for getting the details of a single contract
 * import job by ID, including OCR-extracted data and available actions.
 *
 * @param jobId - The ID of the import job to fetch.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 * @see https://app.dev.vestigas.com/redoc#tag/Contract-Import-Jobs/operation/get_import_job_contracts_import_jobs__job_id__get
 */
export const useQueryContractImportJob = (
  jobId: UUID | undefined,
  options?: Omit<
    UseQueryOptions<
      ContractImportJobDetail,
      VestigasHTTPError,
      ContractImportJobDetail
    >,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery(
    getContractImportJobQueryOptions<ContractImportJobDetail>({
      jobId,
      options,
    }),
  );
